import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// ─────────────────────────────────────────────────────────────────────────────
// Free-text search that can hold a patient's name — kept OUT of the URL.
//
// URL-is-state (DESIGN_CONCEPT §3.2) has one named exception: a search term
// that may be PHI. A name in `?q=` lands in browser history, in server and
// proxy logs, in a link pasted into a chat. So a list's search term lives in
// component state instead, mirrored to sessionStorage — which is per tab, gone
// when the tab closes, and cleared on sign-out — so that leaving for a patient
// record and coming Back still shows the list the doctor searched.
//
// A link made before this change may still carry `?q=`: it is read once, moved
// into the tab's state, and stripped from the address bar (replace, so Back
// never returns to the URL that held it).
// ─────────────────────────────────────────────────────────────────────────────

import {
  readPhiSearchTerm as read, writePhiSearchTerm as write, clearPhiSearchTerms, PHI_SEARCH_PREFIX,
} from './phiSearchStore';

export { clearPhiSearchTerms, PHI_SEARCH_PREFIX };

/**
 * `[term, setTerm]` for one list's committed search term.
 * `urlParam` is the legacy query-param name to migrate out of the URL.
 */
export function usePhiSearchTerm(key, { urlParam = 'q' } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const legacy = urlParam ? searchParams.get(urlParam) : null;
  const [term, setTermState] = useState(() => legacy || read(key));

  useEffect(() => {
    if (!legacy) return;
    write(key, legacy);
    setTermState(legacy);
    const next = new URLSearchParams(searchParams);
    next.delete(urlParam);
    setSearchParams(next, { replace: true });
  }, [legacy]); // eslint-disable-line react-hooks/exhaustive-deps

  const setTerm = useCallback((value) => {
    const v = value || '';
    write(key, v);
    setTermState(v);
  }, [key]);

  return [term, setTerm];
}
